import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ApiAuthenticateResponseModel} from '../model/api-authenticate-response.model';
import {Observable, Subject, Subscriber, tap} from 'rxjs';
import {environment} from '../../environment';
import {EmployeeApiAuthenticateRequestModel} from '../model/employee-api-authenticate-request.model';
import {SessionStorageService} from './session-storage.service';
import {EmployeeApiModel} from '../model/employee-api.model';
import {EmployeeEvent} from '../event/employee.event';
import {EmployeeEventTypeEnum} from '../enum/employee-event-type.enum';
import {EmployeeApiDocumentsModel} from '../model/employee-api-documents-model';

/**
 * @class
 * @description This class provides methods to interact with the Prism HR Employee service.
 * @export
 * @name EmployeeService
 * @service
 */
@Injectable({

  // We'll want this service to be provided as a singleton.
  providedIn: 'root'
})
export class EmployeeService {

  /**
   * @description This constant property contains the unique identifying name of the PrismHR Employee session in the session storage provider.
   * @memberof EmployeeService
   * @name EmployeeService.SessionStorageServiceKey
   * @property
   * @public
   * @readonly
   * @static
   * @type {string}
   */
  public static readonly SessionStorageServiceKey: string = 'syncstream-prism-hr-employee-session';

  /**
   * @description This property contains the event source for the PrismHR Employee service provider.
   * @memberof EmployeeService
   * @name EmployeeService#eventSource
   * @private
   * @property
   * @type {Subject<EmployeeEvent>}
   */
  private eventSource: Subject<EmployeeEvent> = new Subject<EmployeeEvent>();

  /**
   * @description This property contains the interval for the PrismHR Employee session expiration.
   * @memberof EmployeeService
   * @name EmployeeService#intervalSessionExpired
   * @private
   * @property
   * @type {NodeJS.Timeout}
   */
  private intervalSessionExpired: any = null;

  /**
   * @description This property contains the interval for the impending PrismHR employee session expiration.
   * @memberof EmployeeService
   * @name EmployeeService#intervalSessionExpiring
   * @private
   * @property
   * @type {NodeJS.Timeout}
   */
  private intervalSessionExpiring: any = null;

  /**
   * @description This property contains the events observable for the PrismHR Employee service provider.
   * @memberof EmployeeService
   * @name EmployeeService#events
   * @public
   * @property
   * @type {Observable<EmployeeEvent>}
   */
  public events: Observable<EmployeeEvent> = this.eventSource.asObservable();

  /**
   * @constructor
   * @description This method instantiates a new instance of our PrismHR Employee service provider with injected dependencies.
   * @param {HttpClient} httpClient The injected instance of our HTTP Client service provider.
   * @param {SessionStorageService} sessionStorageService The injected instance of our session storage service provider.
   * @memberof EmployeeService
   * @name EmployeeService#constructor
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our HTTP Client service provider.
     * @memberof EmployeeService
     * @name EmployeeService#httpClient
     * @private
     * @readonly
     * @type {HttpClient}
     */
    private readonly httpClient: HttpClient,
    /**
     * @description This property contains the injected instance of our session storage service provider.
     * @memberof EmployeeService
     * @name EmployeeService#sessionStorageService
     * @private
     * @readonly
     * @type {SessionStorageService}
     */
    private readonly sessionStorageService: SessionStorageService
  ) {
  }

  /**
   * @description This method clears the current session from the session storage.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#clearSession
   * @private
   * @returns {void}
   */
  private clearSession(): void {

    // Reset the session expiration interval if it exists.
    if (this.intervalSessionExpired) {

      // Clear the session expiration interval.
      clearInterval(this.intervalSessionExpired);

      // Reset the session expiration interval.
      this.intervalSessionExpired = null;
    }

    // Reset the session expiring interval if it exists.
    if (this.intervalSessionExpiring) {

      // Clear the session expiring interval.
      clearInterval(this.intervalSessionExpiring);

      // Reset the session expiring interval.
      this.intervalSessionExpiring = null;
    }

    // Clear the session from the session storage.
    this.sessionStorageService.delete(EmployeeService.SessionStorageServiceKey);
  }

  /**
   * @description This method emits an event for the PrismHR Employee service provider.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#emitEvent
   * @param {EmployeeEvent} event The event to emit.
   * @private
   * @returns {void}
   */
  private emitEvent(event: EmployeeEvent): void {

    // Check for an event source then emit the event.
    if (this.eventSource) this.eventSource.next(event);
  }

  /**
   * @async
   * @description This method asynchronously retrieves the details for the authenticated employee session from the .NET Backend API which authenticates the request back to PrismHR.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#details
   * @public
   * @returns {Observable<EmployeeApiModel>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public details(): Observable<EmployeeApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<EmployeeApiModel>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/employee'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      }
    );
  }

  /**
   * @description This method ensures that a currently authenticated employee session exists.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#ensureSession()
   * @public
   * @returns {ApiAuthenticateResponseModel} The current authenticated employee session.
   * @throws {Error} When there isn't currently an authenticated employee session.
   */
  public ensureSession(): ApiAuthenticateResponseModel {

    // Localize the existing session from the session storage provider.
    const session: ApiAuthenticateResponseModel | undefined = this.sessionStorageService.load<ApiAuthenticateResponseModel>(EmployeeService.SessionStorageServiceKey);

    // Ensure we have a session to sign out, throw an error if we don't.
    if (!session) throw new Error('No existing employee session.');

    // Ensure the session hasn't expired.
    if (this.sessionStorageService.expired<ApiAuthenticateResponseModel>(EmployeeService.SessionStorageServiceKey)) {

      // Clear any existing session data.
      this.clearSession();

      // Fire the session-expired event.
      this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionExpired));

      // We're done, throw an error indicating the session has expired.
      throw new Error('The existing employee session has expired.');
    }

    // We're done, return the session.
    return session;
  }

  /**
   * @async
   * @description This method asynchronously lists the documents for the authenticated employee session with the .NET Backend API which authenticates the request back to PrismHR.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#listDocuments
   * @param {number} taxYear The tax year to list the documents for.
   * @public
   * @returns {Observable<EmployeeApiDocumentModel[]>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public listDocuments(taxYear: number): Observable<EmployeeApiDocumentsModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<EmployeeApiDocumentsModel>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/employee/:taxYear/documents', {taxYear}),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously loads a document for the authenticated employee session from the .NET Backend API.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#loadDocument
   * @param {string} objectId The unique identifier of the document object.
   * @param {string} fileName The name of the document file.
   * @public
   * @returns {Observable<string>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public loadDocument(objectId: string, fileName: string): Observable<string> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/employee/:objectId/document/:fileName', {objectId: btoa(objectId), fileName}),
      {

        // Set the response type into the request options.
        responseType: 'text',

        // Set the with credentials flag into the request options.
        withCredentials: true
      }
    );
  }

  /**
   * @async
   * @description This method asynchronously authenticates an incoming PrismHR SSO Employee Session with the .NET Backend API which authenticates the request back to PrismHR.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#signIn
   * @param {string} peoId The unique identifier of the PEO from PrismHR.
   * @param {string} sessionKey The unique session key of the authenticated employee from PrismHR.
   * @public
   * @returns {Observable<ApiAuthenticateResponseModel>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public signIn(peoId: string, sessionKey: string): Observable<ApiAuthenticateResponseModel> {

    // Check for a local environment and valid existing session
    if (environment.local) {

      // Try to localize the session
      try {

        // Localize the session from the session storage provider.
        const session: ApiAuthenticateResponseModel = this.ensureSession();

        // Emit our session-start event.
        this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionStart));

        // We're done, return the existing session.
        return new Observable<ApiAuthenticateResponseModel>(
          (subscriber: Subscriber<ApiAuthenticateResponseModel>) => subscriber.next(session));
      }

        // Handle any errors that occur during the sign-in process.
      catch (_) {

        // Clear any existing session data.
        this.clearSession();
      }
    }

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<ApiAuthenticateResponseModel>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/employee/sign-in'),

      // The body of the request.
      {
        // Set the unique identifier of the PEO from PrismHR into the request body.
        peoId,

        // Set the persistent session flag into the request body.
        persistent: false,

        // Set the unique session key of the authenticated employee from PrismHR into the request body.
        token: sessionKey
      } as EmployeeApiAuthenticateRequestModel
    ).pipe(tap({

      // Handle the completion of the sign-in process.
      complete: (): void => {

        // Set the session timeout interval into the instance.
        this.intervalSessionExpired = setInterval(() =>
            this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionExpired)),
          this.sessionStorageService.expiresIn(EmployeeService.SessionStorageServiceKey));

        // Set the impending session timeout interval into the instance.
        this.intervalSessionExpiring = setInterval(() =>
            this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionExpiring)),
          this.sessionStorageService.expiresIn(EmployeeService.SessionStorageServiceKey) - 90000);

        // We're done, emit the session-start event.
        this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionStart));
      },

      // Handle any errors that occur during the sign-in process.
      error: (_: Error): void => this.clearSession(),

      // Handle the session creation.
      next: (r: ApiAuthenticateResponseModel): void => {

        // Clear any existing session storage.
        this.clearSession();

        // If the session sign-in wasn't successful, throw an error with the message from the response.
        if (!r.successful) throw new Error(r.error);

        // Otherwise, store the authenticated employee session in the session storage.
        else this.sessionStorageService.save(EmployeeService.SessionStorageServiceKey, r);
      }
    }));
  }

  /**
   * @async
   * @description This method asynchronously signs out the current authenticated employee session with the .NET Backend API which authenticates the request back to PrismHR.
   * @memberof EmployeeService
   * @method
   * @name EmployeeService#signOut
   * @public
   * @returns {Observable<void>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public signOut(): Observable<void> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<void>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/employee/sign-out'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      }).pipe(tap({

        // Handle the completion of the sign-out process.
        complete: (): void => {

          // Clear any existing session data.
          this.clearSession();

          // We're done, emit the session-end event.
          this.emitEvent(new EmployeeEvent(EmployeeEventTypeEnum.SessionEnd));
        }
      }
    ));
  }
}
